import React, { useEffect, useState, useMemo } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, PencilSimple, Trash, MagnifyingGlass, Heart, ArrowCounterClockwise, Warning, X } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminListPatients, adminCreatePatient, adminUpdatePatient, adminDeletePatient,
  adminRestorePatient, adminHardDeletePatient,
  adminListRooms, adminListOrgs, adminListDevices, adminUpdateDevice, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ArchiveToggle from '../../components/admin/ArchiveToggle';
import { Field, inputCls, monoCls, selectCls, Modal } from '../../components/admin/Field';
import { loadDeviceCatalog, deviceLabelFrom, isWearableType } from '../../lib/deviceCatalog';

export default function AdminPatients() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current, isSuperAdmin } = useOrg();
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [viewArchived, setViewArchived] = useState(false);

  const allowed = user?.role === 'admin';

  const reload = async () => {
    setLoading(true);
    try {
      const [{ items }, { items: r }] = await Promise.all([
        adminListPatients({ archived: viewArchived }),
        adminListRooms().catch(() => ({ items: [] })),
      ]);
      const filtered = viewArchived ? items.filter((p) => p.deleted_at) : items;
      setItems(filtered);
      setRooms(r);
    } catch (err) {
      toast.error(formatAdminError('List patients', err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    reload();
    if (isSuperAdmin) adminListOrgs().then(({ items }) => setOrgs(items)).catch(() => setOrgs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, isSuperAdmin, viewArchived]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((p) => (p.name || `${p.first_name || ''} ${p.last_name || ''}`).toLowerCase().includes(needle));
  }, [items, q]);

  const onSave = async (form, deviceSync) => {
    setBusy(true);
    try {
      let saved;
      if (editing === 'new') {
        saved = await adminCreatePatient(form);
        toast.success(t('admin_patient_created'));
      } else {
        saved = await adminUpdatePatient(editing.id, form);
        toast.success(t('admin_patient_updated'));
      }
      // Sync device assignments (device.assigned_patient_id) after the patient
      // exists so a freshly-created patient can receive devices too.
      const pid = saved?.id || editing?.id;
      if (deviceSync && pid) {
        await syncPatientDevices(pid, deviceSync).catch((e) => toast.error(formatAdminError('Assign devices', e)));
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError(editing === 'new' ? 'Create patient' : 'Update patient', err));
    } finally {
      setBusy(false);
    }
  };

  // Add newly-checked devices to this patient (clearing any room), remove the
  // ones that were unchecked. Partial device PATCH is supported by the backend.
  const syncPatientDevices = async (patientId, { selected, initial }) => {
    const selSet = new Set(selected);
    const initSet = new Set(initial);
    for (const id of selected) if (!initSet.has(id)) await adminUpdateDevice(id, { assigned_patient_id: patientId, assigned_room_id: null });
    for (const id of initial) if (!selSet.has(id)) await adminUpdateDevice(id, { assigned_patient_id: null });
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await adminDeletePatient(confirm.id);
      toast.success(t('admin_patient_deleted'));
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Delete patient', err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (p) => {
    setBusy(true);
    try {
      await adminRestorePatient(p.id);
      toast.success(t('admin_restored'));
      reload();
    } catch (err) {
      toast.error(formatAdminError('Restore patient', err));
    } finally {
      setBusy(false);
    }
  };

  const onHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeletePatient(hardConfirm.id);
      toast.success(t('admin_hard_deleted'));
      setHardConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Permanently delete patient', err));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_patients_title')}>
        <Toaster position="top-right" richColors />
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title={t('admin_patients_title')}
      description={isSuperAdmin ? t('admin_patients_sub_super') : t('admin_patients_sub_org', { org: current?.name || '—' })}
    >
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text" placeholder={t('search')} value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-patients-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone_line bg-white text-sm focus:outline-none focus:border-sage"
          />
        </div>
        {!viewArchived && (
          <button type="button" onClick={() => setEditing('new')} data-testid="admin-patient-new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700">
            <Plus size={16} weight="bold" /> {t('admin_patient_new')}
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex items-center mb-3">
          <ArchiveToggle value={viewArchived} onChange={setViewArchived} />
        </div>
      )}

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">
            {viewArchived ? t('admin_no_archived') : t('no_results')}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-patients-table">
            <thead className="bg-bg-alt text-ink-soft text-left text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-3 font-medium">{t('admin_field_name')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_birth_date')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_room')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_status')}</th>
                <th className="px-5 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone_line">
              {filtered.map((p) => (
                <tr key={p.id} className={`hover:bg-bg-alt/40 ${viewArchived ? 'opacity-70' : ''}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                        <Heart size={16} weight="duotone" />
                      </div>
                      <div className="font-medium text-ink">
                        {p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—'}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-ink-soft">
                    {p.birth_date ? new Date(p.birth_date).toISOString().slice(0, 10) : '—'}
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{p.room || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full ${
                      viewArchived ? 'bg-terracotta-100 text-terracotta-700' : 'bg-sage-100 text-sage-700'
                    }`}>
                      {viewArchived ? t('admin_view_archived').toLowerCase() : (p.status || 'ok')}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {viewArchived ? (
                      <>
                        <button onClick={() => onRestore(p)} disabled={busy} data-testid={`admin-patient-restore-${p.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-sage-100 hover:text-sage-700 mr-1 disabled:opacity-50"
                          aria-label={t('admin_restore')} title={t('admin_restore')}>
                          <ArrowCounterClockwise size={16} />
                        </button>
                        <button onClick={() => setHardConfirm(p)} disabled={busy} data-testid={`admin-patient-hard-delete-${p.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700 disabled:opacity-50"
                          aria-label={t('admin_hard_delete')} title={t('admin_hard_delete')}>
                          <Warning size={16} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditing(p)} data-testid={`admin-patient-edit-${p.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink mr-1" aria-label={t('edit')}>
                          <PencilSimple size={16} />
                        </button>
                        <button onClick={() => setConfirm(p)} data-testid={`admin-patient-delete-${p.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700" aria-label={t('admin_delete')}>
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <PatientForm
          patient={editing === 'new' ? null : editing}
          rooms={rooms}
          orgs={orgs}
          showOrgPicker={isSuperAdmin}
          activeOrg={current}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t('admin_patient_delete_title')}
        description={confirm ? t('admin_patient_delete_confirm', { name: confirm.name }) : ''}
        confirmLabel={t('admin_delete')}
        danger busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={!!hardConfirm}
        title={t('admin_hard_delete_title')}
        description={hardConfirm ? t('admin_hard_delete_confirm', { name: hardConfirm.name || `${hardConfirm.first_name || ''} ${hardConfirm.last_name || ''}`.trim() }) : ''}
        confirmLabel={t('admin_hard_delete')}
        danger busy={busy}
        onCancel={() => setHardConfirm(null)}
        onConfirm={onHardDelete}
      />
    </AdminPage>
  );
}

function PatientForm({ patient, rooms, orgs, showOrgPicker, activeOrg, onCancel, onSave, busy }) {
  const { t } = useI18n();
  const ec0 = patient?.emergency_contacts?.[0] || {};
  const [form, setForm] = useState({
    name: patient?.name || `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() || '',
    birth_date: patient?.birth_date ? patient.birth_date.slice(0, 10) : '',
    photo: patient?.photo || patient?.avatar_url || '',
    notes: patient?.notes || patient?.caregiver_notes || '',
    status: patient?.status || 'ok',
    assigned_room_id: patient?.assigned_room_id || patient?.room_id || '',
    organization_id: patient?.organization_id || activeOrg?.id || '',
    ec_name: ec0.name || patient?.emergency_contact_name || '',
    ec_phone: ec0.phone || patient?.emergency_contact_phone || '',
    ec_relationship: ec0.relationship || '',
  });

  // ---- Assignable wearable devices (bracelets) for this patient ----
  const [devices, setDevices] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [initialDevices, setInitialDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [pickDeviceId, setPickDeviceId] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingDevices(true);
    Promise.all([
      loadDeviceCatalog().catch(() => []),
      adminListDevices().then((r) => r.items).catch(() => []),
    ]).then(([cat, devs]) => {
      if (cancelled) return;
      setCatalog(cat);
      const wearables = (devs || []).filter((d) => !d.deleted_at && isWearableType(cat, d.type));
      setDevices(wearables);
      const mine = wearables
        .filter((d) => (d.assigned_patient_id || d.resident_id) === patient?.id)
        .map((d) => d.id);
      setSelectedDevices(mine);
      setInitialDevices(mine);
    }).finally(() => { if (!cancelled) setLoadingDevices(false); });
    return () => { cancelled = true; };
  }, [patient?.id]);

  const toggleDevice = (id) =>
    setSelectedDevices((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const availableDevices = devices.filter((d) => !selectedDevices.includes(d.id));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const body = {
      name: form.name.trim(),
      birth_date: form.birth_date || null,
      photo: form.photo || null,
      caregiver_notes: form.notes || null,
      notes: form.notes || null,
      status: form.status,
      assigned_room_id: form.assigned_room_id || null,
      emergency_contacts: form.ec_name || form.ec_phone
        ? [{ name: form.ec_name, phone: form.ec_phone, relationship: form.ec_relationship }]
        : [],
    };
    if (showOrgPicker && form.organization_id) body.organization_id = form.organization_id;
    onSave(body, { selected: selectedDevices, initial: initialDevices });
  };

  return (
    <Modal onCancel={onCancel} wide>
      <form onSubmit={submit} data-testid="admin-patient-form" className="space-y-4">
        <h2 className="font-heading text-2xl text-ink">
          {patient ? t('admin_patient_edit_title') : t('admin_patient_new_title')}
        </h2>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_name')} required>
            <input type="text" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="admin-patient-name" className={inputCls} />
          </Field>
          <Field label={t('admin_field_birth_date')}>
            <input type="date" value={form.birth_date}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              data-testid="admin-patient-birth-date" className={monoCls} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_room')}>
            <select value={form.assigned_room_id}
              onChange={(e) => setForm({ ...form, assigned_room_id: e.target.value })}
              data-testid="admin-patient-room" className={selectCls}>
              <option value="">{t('admin_field_room_none')}</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.room_name}</option>)}
            </select>
          </Field>
          <Field label={t('admin_field_status')}>
            <select value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="admin-patient-status" className={selectCls}>
              <option value="ok">OK</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </Field>
        </div>

        <Field label={t('admin_field_photo')} hint={t('admin_field_photo_hint')}>
          <input type="url" value={form.photo}
            onChange={(e) => setForm({ ...form, photo: e.target.value })}
            data-testid="admin-patient-photo" placeholder="https://…" className={monoCls} />
        </Field>

        <Field label={t('admin_field_notes')}>
          <textarea rows={3} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="admin-patient-notes" className={inputCls} />
        </Field>

        <div className="border-t border-stone_line pt-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
            {t('admin_field_emergency_contact')}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label={t('admin_field_ec_name')}>
              <input type="text" value={form.ec_name}
                onChange={(e) => setForm({ ...form, ec_name: e.target.value })}
                data-testid="admin-patient-ec-name" className={inputCls} />
            </Field>
            <Field label={t('admin_field_ec_phone')}>
              <input type="tel" value={form.ec_phone}
                onChange={(e) => setForm({ ...form, ec_phone: e.target.value })}
                data-testid="admin-patient-ec-phone" className={monoCls} />
            </Field>
            <Field label={t('admin_field_ec_relationship')}>
              <input type="text" value={form.ec_relationship}
                onChange={(e) => setForm({ ...form, ec_relationship: e.target.value })}
                data-testid="admin-patient-ec-relationship" className={inputCls}
                placeholder="daughter / son / spouse" />
            </Field>
          </div>
        </div>

        <div className="border-t border-stone_line pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
              {t('assigned_devices')}
            </div>
            {!devicePickerOpen && availableDevices.length > 0 && (
              <button
                type="button"
                onClick={() => { setPickDeviceId(''); setDevicePickerOpen(true); }}
                data-testid="admin-patient-assign-device"
                className="inline-flex items-center gap-1 text-xs text-sage hover:text-sage-600"
              >
                <Plus size={14} weight="bold" /> {t('assign_device')}
              </button>
            )}
          </div>

          {devicePickerOpen && (
            <div className="border border-stone_line rounded-xl p-3 bg-bg-alt/40 space-y-3 animate-fade-up mb-3">
              <select
                value={pickDeviceId}
                onChange={(e) => setPickDeviceId(e.target.value)}
                data-testid="admin-patient-device-select"
                className={selectCls}
              >
                <option value="">{t('select_device')}</option>
                {availableDevices.map((d) => {
                  const other = (d.assigned_patient_id || d.resident_id) && (d.assigned_patient_id || d.resident_id) !== patient?.id;
                  return (
                    <option key={d.id} value={d.id}>
                      {deviceLabelFrom(catalog, d.type)}{d.mac ? ` · ${d.mac}` : ''}{other ? ` (${t('admin_patient_device_reassign')})` : ''}
                    </option>
                  );
                })}
              </select>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setDevicePickerOpen(false); setPickDeviceId(''); }}
                  data-testid="admin-patient-device-picker-cancel"
                  className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  disabled={!pickDeviceId}
                  onClick={() => { toggleDevice(pickDeviceId); setDevicePickerOpen(false); setPickDeviceId(''); }}
                  data-testid="admin-patient-device-picker-add"
                  className="px-4 py-2 rounded-lg bg-sage hover:bg-sage-600 text-white text-sm transition-colors disabled:opacity-50"
                >
                  {t('add')}
                </button>
              </div>
            </div>
          )}

          {loadingDevices ? (
            <div className="text-sm text-ink-muted">{t('loading')}</div>
          ) : selectedDevices.length === 0 ? (
            <div className="text-sm text-ink-muted">{t('admin_patient_no_devices_assigned')}</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="admin-patient-devices">
              {selectedDevices.map((id) => {
                const d = devices.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    data-testid={`admin-patient-device-chip-${id}`}
                    className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full bg-sage-100 text-sage-700 text-xs font-medium"
                  >
                    {d ? deviceLabelFrom(catalog, d.type) : id}
                    {d?.mac ? <span className="text-sage-600/70 font-mono">{d.mac}</span> : null}
                    <button
                      type="button"
                      onClick={() => toggleDevice(id)}
                      data-testid={`admin-patient-device-remove-${id}`}
                      className="p-0.5 rounded-full hover:bg-sage-600 hover:text-white transition-colors"
                      aria-label="remove"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {showOrgPicker && orgs.length > 0 && (
          <Field label={t('admin_field_organization')}>
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              data-testid="admin-patient-org" className={selectCls}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="admin-patient-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={busy || !form.name.trim()} data-testid="admin-patient-save"
            className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
            {busy ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
